<template>
  <div class="gs-app gm-mail-tags-refresh gm-refresh-tables">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="flex mb-4 mx-4">
      <div class="w-full gm-mail-tags-refresh__workspace">
        <navigation-tabs
          class="gm-nav-tag-tabs absolute"
          :nav_tabs="navTabs"
        ></navigation-tabs>
        <server-table
          :key="tabelKey"
          class="text-center groovemail-tag-table gs-refresh-controls"
          url="/tags"
          ref="serverTable"
          tableRef="tagsTable"
          :slotData="slotData"
          :tableColumns="mailTagsTableColumns"
          :tableOptions="mailTagsTableOptions"
          :firstSelectedFilter="categoryFilter"
          :firstFilterData="categoryFilters"
          :emptyStateData="emptyStateData"
          firstFilterName="frontend_groovemember_configmenu_template_category"
          @firstFilterUpdated="categoryFilterUpdated"
          tableTitle="frontend_groovemail_tags_route_page_title"
          addNewBtnText="frontend_groovemail_mail_tag_new_tag"
          @addNewData="openCreateMailtag"
          @loaded="hideLoader()"
          @loading="showLoader()"
        >
        </server-table>

        <gs-named-modal class="gs-refresh-controls"
          name="mailtag-modal"
          :title="
            modalType === 'add'
              ? $t('frontend_groovemail_contacts_step4_create_newtag')
              : $t('frontend_groovemail_tags_edit_tag')
          "
        >
          <create-tag
            :type="modalType"
            :mail_tag_id="mailTagId"
            :mail_tag_data="mailTagData"
            @fetchTags="fetchTagsListing(modalType === 'add' ? 1 : null)"
            @showLoader="showLoader"
            @hideLoader="hideLoader"
            @fetchCategories="fetchCategoryListing"
            @closeCreateTagsModal="closeMailtagModal"
            @closeEditTagsModal="closeMailtagModal"
          ></create-tag>
        </gs-named-modal>
      </div>
    </div>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete-mail-tag`"
      :name="`confirmation-delete-mail-tag`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_swal_revert_message')"
      @confirm="deleteMailTag"
      @close="closeDeleteTagModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import Notify from '@groovepages/gd-ui-kit/notify';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import { isKeyTimedOut } from '@/utils/localStorageHelper';
import { refreshMailObject } from '@/utils/refreshHelper';
import { tagUrl, categoryUrl } from '@/utils/urls.js';
import { getData, deleteData } from '@/utils/apiHelper.js';
import CreateTag from '@/components/create-tags.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import themeConfig from '@/theme/themeConfig.js';
import showAlert from '@/components/sweet-alert-popup.js';

let self;

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_tags_route_page_title') };
  },
  data() {
    return {
      tabelKey: 0,
      totalTags: 0,
      modalType: 'add',
      categories: [],
      mailTagId: null,
      isLoading: false,
      mailTagData: {
        name: '',
        description: '',
        category: [],
        categoryname: '',
      },
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_tags_route_page_title',
        emptyStateSubHeading: 'frontend_groovemail_tags_have_no_data',
        emptyStateBtnText: 'frontend_groovemail_mail_tag_new_tag',
      },
      categoryFilter: '',
      navTabs: [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_lists',
          tabRoute: 'Lists',
        },
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_tags',
          tabRoute: 'Tags',
        },
        {
          tabName: 'frontend_sites_template_categories',
          tabRoute: 'Category',
        },
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_segments',
          tabRoute: 'Segments',
        },
      ],
      mailTagsTableColumns: [
        'refresh',
        'name',
        'id',
        'activeleads_count',
        'categoriesNames',
        'actions',
      ],
      mailTagsTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name', 'activeleads_count'],
        filterable: ['name', 'categoriesNames', 'activeleads_count'],
        headings: {
          refresh: () => '',
          id: () => this.$t('frontend_groovemail_mail_tags_tag_id'),
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          categoriesNames: () =>
            this.$t('frontend_groovemail_mail_tag_category'),
          activeleads_count: () =>
            this.$t('frontend_groovemail_tags_category_listing_lead_tagged'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
        pagination: { chunk: 5 },
        requestFunction(data) {
          self = this;
          const requestParams = { ...data };
          if (!(requestParams && requestParams.orderBy)) {
            requestParams.orderBy = 'created_at';
            requestParams.ascending = 0;
          }

          // check if the category id is present in the query params and get the tags based on the category id
          if (this.$route.query.catId) {
            requestParams.category_id = this.$route.query.catId;
          }
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
            });
        },
        responseAdapter: ({ data }) => {
          const mailTagTableData = data?.data.map((row) => {
            return {
              ...row,
              disabled: isKeyTimedOut('mailTag', row.id),
            };
          });
          self.totalTags = data?.total;
          return {
            data: mailTagTableData,
            count: data.total,
          };
        },
      },
      mouseHoveredOnTable: false,
    };
  },

  components: {
    CreateTag,
    NavigationTabs,
    ServerTable,
  },

  methods: {
    // Shows loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // Hides loader
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // Shows success notification when tag Id is copied successfully
    onCopyEmbedCode() {
      Notify.success(
        this.$t('frontend_groovemail_mail_tags_tag_id_copy_success')
      );
    },

    // Shows error notification when tag Id is not copied successfully
    onErrorEmbedCode() {
      Notify.fail(this.$t('frontend_groovemail_mail_tags_tag_id_copy_failure'));
    },

    /**
     * Redirects to Contacts page that displays details of all contacts present in that Tag.
     * @param {object} tag - contains detail of the tag.
     */
    goToFilteredLeads(tag) {
      this.$router.push({
        name: 'Leads2',
        query: { tagId: tag.id, tagName: tag.name },
      });
    },

    // Opens create-mailtag-modal to add tag.
    openCreateMailtag() {
      this.modalType = 'add';
      this.mailTagData.name = '';
      this.mailTagData.description = '';
      this.mailTagData.category = [];
      this.mailTagData.categoryname = '';
      this.openMailTagModal();
    },

    // Opens create/edit tag-modal
    openMailTagModal() {
      this.$root.$emit('modal-open', 'mailtag-modal');
    },

    // Closes create/edit tag-modal
    closeMailtagModal() {
      this.$root.$emit('modal-close', 'mailtag-modal');
    },

    /**
     * Opens edit mailtag popup.
     * @param {string} id unique id of a tag.
     */
    async openEditMailtag(id) {
      this.modalType = 'edit';
      this.mailTagId = id;
      try {
        const response = await getData(`${tagUrl.tag}/${id}`);
        const tagData = response?.data;
        if (tagData) {
          this.mailTagData.name = tagData?.name;
          this.mailTagData.description = tagData?.description;
          this.mailTagData.categoryname = '';
          const categoryArr = [];
          tagData?.categories.map((value, key) => {
            categoryArr.push(value.category_id);
          });
          this.mailTagData.category = categoryArr;
        }
        this.openMailTagModal();

      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    // Calls API to fetch category listing.
    async fetchCategoryListing() {
      try {
        const categoryData = await getData(categoryUrl);
        this.categories = categoryData?.data;
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    /**
     * Used to delete a tag.
     * @param {string} pageNo page number of tags table.
     */
    async fetchTagsListing(pageNo) {


      // if table is not available, then refresh the table.
      if (!this.$refs.serverTable.$refs.tagsTable) {
        // this is to refresh the table when the list is updated.
        this.tabelKey += 1;

        // hide loader
        this.hideLoader();
        return;
      }

      const limit = this.mailTagsTableOptions.perPage;
      const page = pageNo || this.$refs.serverTable.$refs.tagsTable.Page;
      const query = this.$refs.serverTable.$refs.tagsTable.filteredQuery;

      try {
        let tagsListingUrl = `/tags?query=${query}&limit=${limit}&page=${page}&ascending=0&byColumn=0`;
  
        if (this.categoryFilter) {
          tagsListingUrl += `&category_id=${this.categoryFilter}`;
        }
        const tagData = await getData(tagsListingUrl);
        this.totalTags = tagData?.data?.total;
        this.$refs.serverTable.updateData(
          tagData?.data?.data,
          tagData?.data?.total
        );
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    // open delete tag modal
    async openDeleteTagModal(id) {
      // set the tag id
      this.mailTagId = id;

      // open the modal
      this.$refs[`confirmation-delete-mail-tag`].open();
    },

    // close delete tag modal
    closeDeleteTagModal() {
      // reset the tag id
      this.mailTagId = null;

      // close the modal
      this.$refs[`confirmation-delete-mail-tag`].close();
    },

    // Deletes a tag.
    async deleteMailTag() {
      try {
        await deleteData(`${tagUrl.tag}/${this.mailTagId}`);
        await this.fetchTagsListing();

        // check if the table data = 1
        if (this.$refs.serverTable.$refs.tagsTable.data.length < 1) {
          this.tabelKey += 1;
        }
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    // Replaces route with different query on change of category filter
    async categoryFilterUpdated(event) {
      this.categoryFilter = event;

      if (this.categoryFilter === '' || !this.categoryFilter) {
        this.$router.replace({ name: 'Tags' }).catch(() => {});
      } else {
        this.$router
          .replace({ name: 'Tags', query: { catId: this.categoryFilter } })
          .catch(() => {});
      }
      await this.fetchTagsListing();
    },

    getRowIndex(tagData, id) {
      return tagData.findIndex((element) => element.id === id);
    },

    updateRowData(id, data) {
      const mailTagData = this.$refs.serverTable.$refs.tagsTable?.data;
      const rowIndex = this.getRowIndex(mailTagData, id);
      const { categoriesNames } = mailTagData[rowIndex];
      mailTagData[rowIndex] = {
        id,
        ...data,
        categoriesNames,
        disabled: true,
      };
      this.$refs.serverTable.updateData(mailTagData, this.totalTags);
    },

    async refreshMailTag(id) {
      try {
        const url = `${tagUrl.tagRefresh}/${id}`;
        const response = await refreshMailObject(id, url, 'mailTag');
        if (response) {
          this.updateRowData(id, response);
        }

      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },
  },

  // Fetches categories to be listed in the dropdown
  async created() {
    self = this;

    // check if the category id is present in the query params and set the category filter value
    if (this.$route.query.catId) {
      this.categoryFilter = this.$route.query.catId;
    }

    await this.fetchCategoryListing();
  },

  computed: {
    /**
     * Returns categories data for category filter dropdown.
     * @return {array} categoryFilters, categories for filter
     */
    categoryFilters() {
      const categoryFilters = [];
      if (this.categories && this.categories.length) {
        this.categories.forEach((element) => {
          categoryFilters.push({ text: element.name, value: element.id});
        });
      }
      return categoryFilters;
    },

    /**
     * Returns slot data for tags table.
     * @return {array} details for slot columns for tags table.
     */
    slotData() {
      return [
        {
          slotName: 'refresh',
          slotType: 'default',
          conditions: () => true,
          callback: (props) => {
            if (!props.row.disabled) {
              this.refreshMailTag(props.row.id);
            }
            return null;
          },
          html: (props) => {
            const tooltipText = props.row.disabled
              ? this.$t('frontend_groovemail_disabled_refresh_button_tooltip')
              : this.$t('frontend_groovemail_refresh_button');

            const iconClass = props.row.disabled
              ? 'h-5 w-5 mt-1 refresh-icon-disabled'
              : 'h-5 w-5 mt-1 refresh-icon text-primary';

            const iconId = props.row.id ? `id="refreshIcon-${props.row.id}"` : '';

            const refreshSvg = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="${iconClass}" ${iconId}>
                <path fill="currentcolor" d="M12,2a10.032,10.032,0,0,1,7.122,3H16a1,1,0,0,0-1,1h0a1,1,0,0,0,1,1h4.143A1.858,1.858,0,0,0,22,5.143V1a1,1,0,0,0-1-1h0a1,1,0,0,0-1,1V3.078A11.981,11.981,0,0,0,.05,10.9a1.007,1.007,0,0,0,1,1.1h0a.982.982,0,0,0,.989-.878A10.014,10.014,0,0,1,12,2Z"/>
                <path fill="currentcolor" d="M22.951,12a.982.982,0,0,0-.989.878A9.986,9.986,0,0,1,4.878,19H8a1,1,0,0,0,1-1H9a1,1,0,0,0-1-1H3.857A1.856,1.856,0,0,0,2,18.857V23a1,1,0,0,0,1,1H3a1,1,0,0,0,1-1V20.922A11.981,11.981,0,0,0,23.95,13.1a1.007,1.007,0,0,0-1-1.1Z"/>
              </svg>
            `;

            return `
              <span class="tooltip-refresh-btn">
                ${refreshSvg}
                <span class="tooltiptext">${tooltipText}</span>
              </span>
            `;
          },
        },
        {
          slotName: 'id',
          slotType: 'copy',
          value: (props) => props.row.id,
          tooltip: {
            content: this.$t('frontend_resource_index_copy_to_clipboard'),
            placement: 'top',
            classes: ['info'],
            targetClasses: ['it-has-a-tooltip'],
            offset: 10,
            delay: {
              show: 200,
              hide: 300,
            },
          },
          onCopyEmbedCode: () => this.onCopyEmbedCode(),
          onErrorEmbedCode: () => this.onErrorEmbedCode(),
        },
        {
          slotName: 'name',
          slotType: 'default',
          conditions: () => true,
          callback: () => null,
          html: (props) => {
            let template = this.$t('frontend_groovemail_not_available');
            if (props.row.name) template = `<p>${props.row.name}</p>`;
            if (props.row.name && props.row.description)
              template += `<p class="tag-desc">${props.row.description}</p>`;

            return template;
          },
        },
        {
          slotName: 'categoriesNames',
          slotType: 'array',
          callback: (categoryId) =>
            this.categoryFilterUpdated(categoryId),
        },
        {
          slotName: 'activeleads_count',
          slotType: 'clickable',
          classes: () => '',
          callback: (props) => this.goToFilteredLeads(props.row),
          defaultValue: 0,
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => true,
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_edit'),
              conditions: () => {
                return true;
              },
              callback: (props) => this.openEditMailtag(props.row.id),
            },
            {
              id: 1,
              name: () => this.$t('frontend_delete'),
              conditions: (row) => {
                return !row.activeleads_count;
              },
              callback: (props) => this.openDeleteTagModal(props.row.id),
            },
          ],
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.groovemail-tag-table {
  .refresh-icon-disabled {
    cursor: pointer;
    color: var(--gs-border-strong);
  }
  .tooltip-refresh-btn {
    position: relative;
    display: inline-block;
    
    .tooltiptext {
      visibility: hidden;
      width: 120px;
      background-color: var(--gs-surface);
      color: var(--gs-foreground);
      text-align: center;
      border: 1px solid var(--gs-border);
      border-radius: var(--gs-radius-sm);
      box-shadow: var(--gs-shadow-md);
      font-size: 12px;
      font-weight: 600;
      padding: 5px 0;
      /* Position the tooltip */
      position: absolute;
      z-index: 1;
      top: 30px;
      left: -50px;
    }

    &:hover .tooltiptext {
      visibility: visible;
    }
  }
  .refresh-icon {
    cursor: pointer;
    color: var(--gs-muted-foreground);
    transition: transform 0.5s ease-in-out, color 120ms ease;

    &:hover {
      color: var(--gs-primary);
    }
    
    &.spin {
      animation: spin 0.5s infinite linear;
    }
  }


  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .VueTables--server table {
    overflow: auto !important;
    td:nth-child(2),
    th:nth-child(2),
    td:nth-child(3),
    th:nth-child(3) {
      width: 20% !important;
    }
    td:nth-child(4),
    th:nth-child(4) {
      width: 17% !important;
    }
    td:nth-child(5),
    th:nth-child(5) {
      width: 35% !important;
    }
    th:nth-child(6) span {
      display: none;
    }
  }
}

.vue-treeselect__multi-value-item {
  border-radius: var(--gs-radius-sm) !important; /* third-party inline radius */
}

.vue-treeselect__value-remove {
  border: none !important;
}


.vue-treeselect__multi-value-label {
  font-size: 14px !important;
}


.groovemail-tag-table .tag-desc {
  word-break: break-all;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: var(--gs-muted-foreground);
}

.gm-nav-tag-tabs {
  top: 37px;
  z-index: 10;
}


// custom style for refresh icon show on hover
#gd-server-table-view .VueTables__table tr {
  .tooltip-refresh-btn svg:not(.spin) {
    transition: opacity .2s ease;
    opacity: 0;
  }

  &:hover .tooltip-refresh-btn svg {
    opacity: 1;
  }
}
</style>

<style scoped>
.gm-mail-tags-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-mail-tags-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-mail-tags-refresh { padding: 18px 12px 32px; }
  .gm-mail-tags-refresh__workspace { padding: 16px; }
}
</style>
